import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, MapPin } from "lucide-react";

import { api } from "../lib/api";

interface LookupResponse {
  employee: {
    oracle_id: string;
    full_name: string;
    title: string | null;
    home_country: string | null;
    family_count: number;
  };
  expires_at: string;
}

type Status = "home" | "relocated" | "in_transit" | "unknown";

export default function CheckIn() {
  const { token = "" } = useParams();
  const lookup = useQuery({
    queryKey: ["checkin-token", token],
    queryFn: () => api.get<LookupResponse>(`/api/checkin/${encodeURIComponent(token)}`),
    retry: false,
  });

  const [status, setStatus] = useState<Status | null>(null);
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [note, setNote] = useState("");
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      api.post<{ status: string }>(`/api/checkin/${encodeURIComponent(token)}/submit`, {
        status,
        country: country || null,
        city: city || null,
        address: address || null,
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
        note: note || null,
      }),
  });

  if (lookup.isPending) {
    return <Centered>Loading…</Centered>;
  }
  if (lookup.error || !lookup.data) {
    return (
      <Centered>
        <div className="text-red-300 text-center">
          This link is invalid or has expired. Please request a new one from your HR contact.
        </div>
      </Centered>
    );
  }

  if (submit.isSuccess) {
    return (
      <Centered>
        <div className="rounded-2xl bg-[var(--color-surface)]/80 ring-1 ring-[var(--color-border)] p-8 text-center max-w-sm w-full">
          <CheckCircle2 className="text-emerald-400 mx-auto" size={56} />
          <div className="text-xl font-semibold text-slate-100 mt-4">Got it.</div>
          <p className="text-sm text-slate-400 mt-1">
            Your check-in has been recorded. Thank you, {lookup.data.employee.full_name.split(" ")[0]}.
          </p>
        </div>
      </Centered>
    );
  }

  const { employee } = lookup.data;
  const canSubmit = status !== null && (status === "home" || country || coords);

  return (
    <Centered>
      <div className="rounded-2xl bg-[var(--color-surface)]/80 ring-1 ring-[var(--color-border)] p-6 max-w-md w-full space-y-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-400">Check in for</div>
          <div className="text-xl font-semibold text-slate-100">{employee.full_name}</div>
          <div className="text-xs text-slate-500">
            {employee.title || "—"} · home: {employee.home_country || "—"}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {(["home", "relocated", "in_transit", "unknown"] as Status[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={`rounded-lg px-3 py-3 text-sm ring-1 transition ${
                status === s
                  ? "bg-sky-500/15 text-sky-200 ring-sky-400/40"
                  : "bg-slate-900/40 text-slate-300 ring-slate-700 hover:bg-slate-800/60"
              }`}
            >
              {s === "home" && "I'm at home"}
              {s === "relocated" && "I've relocated"}
              {s === "in_transit" && "I'm in transit"}
              {s === "unknown" && "Other / prefer text"}
            </button>
          ))}
        </div>

        {status && status !== "home" && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <input
                placeholder="Country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-3 py-2 text-sm text-slate-100"
              />
              <input
                placeholder="City"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-3 py-2 text-sm text-slate-100"
              />
            </div>
            <input
              placeholder="Address (optional)"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-3 py-2 text-sm text-slate-100"
            />
            <button
              type="button"
              onClick={() => {
                if (!navigator.geolocation) return;
                navigator.geolocation.getCurrentPosition(
                  (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                  () => undefined,
                );
              }}
              className="text-xs inline-flex items-center gap-1 text-sky-300 hover:text-sky-200"
            >
              <MapPin size={12} />
              {coords ? "Location captured ✓" : "Use my current location"}
            </button>
          </div>
        )}

        <textarea
          placeholder="Note for HR (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          className="w-full rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-3 py-2 text-sm text-slate-100 resize-none"
        />

        <button
          disabled={!canSubmit || submit.isPending}
          onClick={() => submit.mutate()}
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-white px-4 py-2.5 font-medium"
        >
          {submit.isPending ? <Loader2 className="animate-spin" size={16} /> : null}
          Submit check-in
        </button>

        {submit.isError && (
          <div className="text-sm text-red-300">{(submit.error as Error).message}</div>
        )}
      </div>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-3 py-6 sm:px-4 sm:py-8">
      {typeof children === "string" ? (
        <div className="text-slate-400">{children}</div>
      ) : (
        children
      )}
    </div>
  );
}
